/**
 * Phase 15 — bulk-import-cases modal.
 *
 * Two-phase: dry-run preview → confirm. Mirrors the team-members modal but
 * adds: confirm-token handshake, warnings list, sample preview, accept-warnings
 * checkbox, conflict-resolution radio, and "View imported batch" CTA after
 * a successful apply.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Upload,
  Download,
  FileSpreadsheet,
  AlertCircle,
  TriangleAlert,
  Loader2,
  Eye,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api, { describeApiError } from "../../lib/api";

export default function BulkImportCasesDialog({
  open,
  onOpenChange,
  onImportComplete,
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [onConflict, setOnConflict] = useState("skip");
  const [acceptWarnings, setAcceptWarnings] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/admin/cases/bulk-import/history")
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setPreview(null);
      setAcceptWarnings(false);
    }
  }, [open]);

  const choose = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
  };

  const downloadSample = () => {
    api
      .get("/admin/cases/bulk-import/sample.csv", { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "cases_sample.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Couldn't download sample CSV."));
  };

  const runDryRun = async () => {
    if (!file) return;
    setPreviewing(true);
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("dry_run", "true");
    fd.append("on_conflict", onConflict);
    try {
      const { data } = await api.post(
        "/admin/cases/bulk-import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data);
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const commit = async () => {
    if (!file || !preview?.confirm_token) return;
    setCommitting(true);
    const fd = new FormData();
    fd.append("csv", file);
    fd.append("dry_run", "false");
    fd.append("on_conflict", onConflict);
    fd.append("confirm_token", preview.confirm_token);
    fd.append("accept_warnings", String(acceptWarnings));
    try {
      const { data } = await api.post(
        "/admin/cases/bulk-import",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const created = data.rows_imported ?? 0;
      const updated = data.rows_updated ?? 0;
      const tms = data.team_members_auto_created ?? 0;
      toast.success(
        `Imported ${created} new case${created === 1 ? "" : "s"} · ` +
          `${updated} updated · ${tms} team member${tms === 1 ? "" : "s"} auto-created.`,
      );
      onImportComplete?.(data);
      onOpenChange(false);
      if (data.import_batch_id) {
        navigate(`/cases?import_batch_id=${data.import_batch_id}`);
      }
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setCommitting(false);
    }
  };

  const errorCount = preview?.errors?.length || 0;
  const warningCount = preview?.warnings?.length || 0;
  // Allow partial commits. Backend handles per-row errors gracefully and
  // commits the valid rows; previously this required `errorCount === 0`,
  // which blocked the user the moment a single bad row appeared.
  const canConfirm =
    !!preview?.confirm_token &&
    !committing &&
    (warningCount === 0 || acceptWarnings) &&
    ((preview?.would_create || 0) + (preview?.would_update || 0) > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        data-testid="bulk-import-cases-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            <span className="inline-flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Bulk-import historical cases
            </span>
          </DialogTitle>
          <DialogDescription>
            Required: position_id (or legacy file_number), date_reported,
            reported_by, issue_type, specific_issue, status.
            Optional: case_details, outcome, dates (including
            <code>date_of_discussion</code> for the HR-employee meeting),
            follow-up fields, partner email, legacy_case_id, notes. If a
            row's position_id is new, supply full_name + department +
            service_line to auto-create the team member.
            <strong>Dates must be MM/DD/YYYY</strong>
            {" "}(e.g. 03/15/2026; ISO YYYY-MM-DD is still accepted).
            Up to 10,000 rows per file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="rounded-lg p-3 text-xs"
            style={{
              backgroundColor: "var(--surface-2)",
              border: "1px solid var(--border-subtle)",
              color: "var(--page-fg-secondary)",
            }}
          >
            <button
              type="button"
              onClick={downloadSample}
              className="inline-flex items-center gap-1.5 underline"
              data-testid="bulk-import-cases-sample-link"
              style={{ color: "var(--page-fg)" }}
            >
              <Download className="h-3 w-3" />
              Download sample CSV (3 rows)
            </button>
          </div>

          <div>
            <label
              className="mb-1.5 block text-xs font-medium"
              htmlFor="bulk-cases-file"
              style={{ color: "var(--page-fg-secondary)" }}
            >
              CSV file
            </label>
            <input
              ref={fileInputRef}
              id="bulk-cases-file"
              type="file"
              accept=".csv,text/csv"
              onChange={choose}
              data-testid="bulk-import-cases-file"
              className="block w-full text-xs"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span style={{ color: "var(--page-fg-secondary)" }}>
              On existing legacy_case_id:
            </span>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="onConflictCases"
                value="skip"
                checked={onConflict === "skip"}
                onChange={() => setOnConflict("skip")}
                data-testid="bulk-import-cases-conflict-skip"
              />
              skip
            </label>
            <label className="inline-flex items-center gap-1.5">
              <input
                type="radio"
                name="onConflictCases"
                value="update"
                checked={onConflict === "update"}
                onChange={() => setOnConflict("update")}
                data-testid="bulk-import-cases-conflict-update"
              />
              update in place
            </label>
            <Button
              size="sm"
              variant="outline"
              onClick={runDryRun}
              disabled={!file || previewing}
              data-testid="bulk-import-cases-preview-btn"
              className="ml-auto"
            >
              {previewing ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="mr-1 h-3.5 w-3.5" />
              )}
              Preview (dry run)
            </Button>
          </div>

          {preview && (
            <div
              className="space-y-3 rounded-lg p-3"
              style={{
                backgroundColor: "var(--surface)",
                border: "1px solid var(--border-subtle)",
              }}
              data-testid="bulk-import-cases-preview"
            >
              <p className="text-xs font-semibold" style={{ color: "var(--page-fg)" }}>
                Preview · {preview.filename}
              </p>
              <div
                className="grid grid-cols-2 gap-2 text-[11px] tabular-nums sm:grid-cols-5"
                style={{ color: "var(--page-fg-secondary)" }}
              >
                <Stat label="Rows" value={preview.total_rows} />
                <Stat label="Will create" value={preview.would_create} accent="success" />
                <Stat label="Will update" value={preview.would_update} accent="warning" />
                <Stat label="Will skip" value={preview.would_skip} accent="muted" />
                <Stat
                  label="Auto TMs"
                  value={preview.team_members_to_create}
                  accent="muted"
                />
              </div>

              {preview.sample_preview?.length > 0 && (
                <details className="text-[11px]">
                  <summary
                    className="cursor-pointer text-[11px] font-medium"
                    style={{ color: "var(--page-fg)" }}
                  >
                    First {preview.sample_preview.length} parsed rows
                  </summary>
                  <ul
                    className="mt-1 space-y-0.5"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {preview.sample_preview.map((s) => (
                      <li key={s.row}>
                        <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                          row {s.row}
                        </span>
                        : #{s.team_member_position_id || s.team_member_file_number} {s.team_member_name} ·{" "}
                        {s.issue_type}/{s.specific_issue} · {s.status} · {s.date_reported}
                        {s.legacy_case_id ? ` · ${s.legacy_case_id}` : ""}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {errorCount > 0 && (
                <div>
                  <p
                    className="inline-flex items-center gap-1 text-[11px] font-semibold"
                    style={{ color: "var(--danger-fg)" }}
                  >
                    <AlertCircle className="h-3 w-3" />
                    {errorCount} row error{errorCount === 1 ? "" : "s"}
                  </p>
                  <ul
                    className="mt-1 max-h-28 space-y-0.5 overflow-y-auto rounded border px-2 py-1.5 text-[11px]"
                    style={{
                      borderColor: "var(--border-subtle)",
                      color: "var(--page-fg-secondary)",
                    }}
                    data-testid="bulk-import-cases-errors"
                  >
                    {preview.errors.map((e, i) => (
                      <li key={i}>
                        <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                          row {e.row}
                        </span>
                        : {e.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {warningCount > 0 && (
                <div>
                  <p
                    className="inline-flex items-center gap-1 text-[11px] font-semibold"
                    style={{ color: "var(--warning-fg)" }}
                  >
                    <TriangleAlert className="h-3 w-3" />
                    {warningCount} warning{warningCount === 1 ? "" : "s"} (legacy data)
                  </p>
                  <ul
                    className="mt-1 max-h-28 space-y-0.5 overflow-y-auto rounded border px-2 py-1.5 text-[11px]"
                    style={{
                      borderColor: "var(--border-subtle)",
                      color: "var(--page-fg-secondary)",
                    }}
                    data-testid="bulk-import-cases-warnings"
                  >
                    {preview.warnings.map((w, i) => (
                      <li key={i}>
                        <span className="font-mono" style={{ color: "var(--page-fg)" }}>
                          row {w.row}
                        </span>
                        : {w.reason}
                      </li>
                    ))}
                  </ul>
                  <label
                    className="mt-2 inline-flex items-center gap-2 text-[11px]"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    <Checkbox
                      checked={acceptWarnings}
                      onCheckedChange={(v) => setAcceptWarnings(!!v)}
                      data-testid="bulk-import-cases-accept-warnings"
                    />
                    Import despite {warningCount} warning
                    {warningCount === 1 ? "" : "s"}
                  </label>
                </div>
              )}

              <p
                className="text-[10px] uppercase tracking-[0.10em]"
                style={{ color: "var(--page-fg-muted)" }}
              >
                Confirm-token expires in {preview.confirm_token_ttl_sec ?? 600}s.
              </p>
            </div>
          )}

          {history.length > 0 && (
            <details
              className="rounded-lg p-2 text-[11px]"
              style={{
                backgroundColor: "var(--surface-2)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <summary
                className="cursor-pointer text-xs font-semibold"
                style={{ color: "var(--page-fg)" }}
              >
                Import history (last {history.length})
              </summary>
              <ul
                className="mt-2 space-y-1"
                data-testid="bulk-import-cases-history"
              >
                {history.map((h, i) => (
                  <li
                    key={h.id || i}
                    className="flex flex-wrap items-center justify-between gap-2"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    <span style={{ color: "var(--page-fg)" }}>
                      {h.filename || "(unnamed)"}
                    </span>
                    <span className="tabular-nums">
                      +{h.rows_imported || 0} ✏{h.rows_updated || 0} ⏭{h.rows_skipped || 0} ⚠{h.warnings_count || 0}
                    </span>
                    <span>
                      {h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}
                    </span>
                    {h.import_batch_id && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 underline"
                        onClick={() => {
                          onOpenChange(false);
                          navigate(`/cases?import_batch_id=${h.import_batch_id}`);
                        }}
                        data-testid={`bulk-import-cases-history-link-${i}`}
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <DialogFooter className="mt-2 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="bulk-import-cases-close"
          >
            Close
          </Button>
          <Button
            onClick={commit}
            disabled={!canConfirm}
            data-testid="bulk-import-cases-confirm"
            className="goodwin-primary-btn text-white"
          >
            {committing ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1 h-4 w-4" />
            )}
            Confirm import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  const accentMap = {
    success: "var(--success-fg)",
    warning: "var(--warning-fg)",
    muted: "var(--page-fg-muted)",
  };
  return (
    <div
      className="rounded-md px-2 py-1.5"
      style={{
        backgroundColor: "var(--surface-2)",
        border: "1px solid var(--border-subtle)",
      }}
    >
      <p
        className="text-[10px] font-semibold uppercase tracking-[0.10em]"
        style={{ color: "var(--page-fg-muted)" }}
      >
        {label}
      </p>
      <p
        className="mt-0.5 font-serif text-base font-semibold"
        style={{ color: accent ? accentMap[accent] : "var(--page-fg)" }}
      >
        {value ?? 0}
      </p>
    </div>
  );
}
